import { useCallback, useRef } from "react";
import type { PointerEventHandler } from "react";

import { clamp } from "../lib/progress";

export type SmileSimulationComparisonBindings = {
  onPointerCancel: PointerEventHandler<HTMLDivElement>;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
  onPointerMove: PointerEventHandler<HTMLDivElement>;
  onPointerUp: PointerEventHandler<HTMLDivElement>;
  updateValue: (value: number) => void;
};

type UseSmileSimulationComparisonOptions = {
  isDragging: boolean;
  onDraggingChange: (isDragging: boolean) => void;
  onValueChange: (value: number) => void;
};

export function useSmileSimulationComparison({
  isDragging,
  onDraggingChange,
  onValueChange,
}: UseSmileSimulationComparisonOptions) {
  const comparisonContainerRef = useRef<HTMLDivElement | null>(null);

  const updateComparisonValue = useCallback(
    (value: number) => {
      onValueChange(Math.round(clamp(value, 0, 100)));
    },
    [onValueChange],
  );

  const updateComparisonValueFromClientX = useCallback(
    (clientX: number) => {
      const comparisonContainer = comparisonContainerRef.current;

      if (!comparisonContainer) {
        return;
      }

      const bounds = comparisonContainer.getBoundingClientRect();

      if (bounds.width <= 0) {
        return;
      }

      const nextValue = ((clientX - bounds.left) / bounds.width) * 100;

      updateComparisonValue(nextValue);
    },
    [updateComparisonValue],
  );

  const onPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();
      updateComparisonValueFromClientX(event.clientX);
      onDraggingChange(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [onDraggingChange, updateComparisonValueFromClientX],
  );

  const onPointerMove = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      if (!isDragging) {
        return;
      }

      updateComparisonValueFromClientX(event.clientX);
    },
    [isDragging, updateComparisonValueFromClientX],
  );

  const onPointerUp = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      onDraggingChange(false);
    },
    [onDraggingChange],
  );

  return {
    bindings: {
      onPointerCancel: onPointerUp,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      updateValue: updateComparisonValue,
    } satisfies SmileSimulationComparisonBindings,
    comparisonContainerRef,
  };
}
